// Shared league stat/format helpers — extracted at the 3rd consumer
// (transactions, trades, awards). FormatStat parity (cStats.asp:263-288):
// AVG/OPS/PCT/FPCT = 3dp no-leading-zero <1; ERA/WHIP/RNG = 2dp;
// else = grouped integer; null/undefined ⇒ "-".

// Verbatim cStats.Positions array (GetPosition(posn)=arr(posn)).
// 0-9 fielding, 10-19 duplicate, 20-30 pitching roles. Out-of-range → ''.
const POSITIONS = [
  'DH', 'P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF',
  'DH', 'P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF',
  'SP1', 'SP2', 'SP3', 'SP4', 'SP5', 'LR', 'MR1', 'MR2', 'SU1', 'SU2', 'CL',
] as const

export function positionLabel(posn: number): string {
  return POSITIONS[posn] ?? ''
}

/** Legacy: "{round} round draft pick (#{pick} overall)". round is the dbo.Ordinal string. */
export function draftPickLabel(round: string | null | undefined, pick: number): string {
  return `${round ?? ''} round draft pick (#${pick} overall)`
}

const CURRENCY_0 = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
})

/**
 * Whole-dollar currency, e.g. `$1,234,567` — faithful to legacy
 * FormatCurrency(n, 0). The single full-dollar salary formatter: the
 * per-surface copies (roster `fullSalary`, player-card `salaryUSD`,
 * bio/history `money`, FA bid hint) all delegate here, each passing its own
 * `empty` string for the null/unset case (`$0`, `—`, …).
 */
export function formatCurrency0(n: number | null | undefined, empty = '$0'): string {
  if (n == null) return empty
  return CURRENCY_0.format(n)
}

/**
 * Compact salary format `X.XXXm` — matches `team/roster`'s `compactSalary`
 * so per-player money reads consistently across roster, players, draft,
 * and trades. Use formatCurrency0 instead for league-level sums and caps
 * where the full dollar figure matters (financials, view).
 */
export function compactSalary(total: number | null | undefined): string {
  if (!total) return '—'
  // Round to nearest $1k first so toFixed(3) doesn't drift at half-thousand values.
  return (Math.round(total / 1_000) / 1_000).toFixed(3) + 'm'
}

/**
 * Team-payroll shorthand `$56.8M` — a whole team's money at a glance, where
 * `formatCurrency0`'s `$56,800,000` is too wide (a menu row, a chip) and
 * `compactSalary`'s per-player `56.800m` is more precision than a payroll
 * comparison needs. One decimal always, so a column of them scans straight.
 */
export function formatPayrollShort(total: number | null | undefined, empty = '—'): string {
  if (total == null) return empty
  return '$' + (Math.round(total / 100_000) / 10).toFixed(1) + 'M'
}

/**
 * Over-cap predicate behind the Total Payroll red highlight (league financials,
 * and the trades "Propose trade to" menu). A missing cap NEVER highlights —
 * legacy `total > NULL` is False in VBScript, so do not coerce it to 0. Both
 * args are already in dollars; the server converts from stored thousands, so
 * there is no client-side scaling.
 */
export function isOverCap(total: number, leagueCap: number | null | undefined): boolean {
  if (leagueCap == null) return false
  return total > leagueCap
}

/** FormatStat ".###"/AVG/OPS/PCT/FPCT: 3 decimals, no leading zero for |v|<1. null ⇒ "-". */
function fmt3(v: number | null | undefined): string {
  if (v == null) return '-'
  const s = Math.abs(v).toFixed(3)
  const sign = v < 0 ? '-' : ''
  return sign + (Math.abs(v) < 1 ? s.replace(/^0/, '') : s)
}
export const fmtAvg = fmt3
export const fmtOps = fmt3
export const fmtPct = fmt3

/** FormatStat ERA/WHIP/RNG: 2 decimals. null ⇒ "-". */
function fmt2(v: number | null | undefined): string {
  if (v == null) return '-'
  return v.toFixed(2)
}
export const fmtEra = fmt2
export const fmtWhip = fmt2
export const fmtRng = fmt2

/** FormatStat Case Else: integer, thousands-grouped. null ⇒ "-". */
export function fmtInt(v: number | null | undefined): string {
  if (v == null) return '-'
  return Math.round(v).toLocaleString('en-US')
}

/**
 * Innings pitched display. The DB stores IP as OUTS (thirds of an inning), so
 * any `sip`-sourced value must be divided by 3 for display — `273` outs ⇒
 * `"91.0"`, `274` ⇒ `"91.1"`, `275` ⇒ `"91.2"` (the .1/.2 = ⅓/⅔ convention).
 * Mirrors cStats.FormatStat's IP branch (and the API's Stats.IP property, which
 * already does this for `ip`-named fields). null/undefined ⇒ "". Use this for
 * EVERY `sip` display — never render raw outs.
 */
export function fmtIp(outs: number | null | undefined): string {
  if (outs == null) return ''
  return `${Math.floor(outs / 3)}.${outs % 3}`
}
